import type { WizardState } from './types'
import { BLUE, SectionBlock, RadioPillGrid, Expandable, FieldRow, Toggle, Stepper, inputStyle, HelpTooltip } from './ui'

// ─── Section icons ────────────────────────────────────────────────────────────
const IcoHouse = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
)
const IcoArea = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15,3 21,3 21,9" />
    <polyline points="9,21 3,21 3,15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
)
const IcoCash = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
)
const IcoCalendar = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)
const IcoKey = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
)

// ─── Data ─────────────────────────────────────────────────────────────────────
const BOLIGTYPE_OPTIONS = [
  { label: 'Lejlighed', value: 'lejlighed' },
  { label: 'Enkeltværelse', value: 'enkeltværelse' },
  { label: 'Ejerlejlighed', value: 'ejerlejlighed' },
  { label: 'Andelsbolig', value: 'andelsbolig' },
  { label: 'Hus', value: 'hus' },
  { label: 'Villa', value: 'villa' },
  { label: 'Rækkehus', value: 'rækkehus' },
  { label: 'Andet', value: 'andet' },
]

const FRI_LEJE_OPTIONS = [
  {
    value: 'post1991',
    label: 'Lejemålet er en beboelseslejlighed i en ejendom, der er taget i brug efter den 31. december 1991, jf. lejelovens § 54, stk. 1, nr. 1',
  },
  {
    value: 'erhverv1991',
    label: 'Lejemålet er en beboelseslejlighed, der den 31. december 1991 lovligt udelukkende benyttedes til erhvervsformål, jf. lejelovens § 54, stk. 1, nr. 2',
  },
  {
    value: 'tagetage',
    label: 'Lejemålet er en beboelseslejlighed eller enkeltværelse, der er nyindrettet i en tagetage, jf. lejelovens § 54, stk. 1, nr. 3',
  },
  {
    value: 'custom',
    label: 'Indstast egen begrundelse',
  },
]

const IcoWash = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="12" cy="13" r="4"/><path d="M7 7h1m2 0h1"/></svg>)
const IcoBox = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21,8 21,21 3,21 3,8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>)
const IcoParking = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/></svg>)
const IcoBike = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M5 17l4-7h5l3 5"/><path d="M10 10h4"/></svg>)
const IcoTree = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14c0-4.97-4-9-5-10C11 5 7 9.03 7 14a5 5 0 0 0 10 0z"/><line x1="12" y1="22" x2="12" y2="19"/></svg>)
const IcoSun = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>)
const IcoPlus = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>)

const ACCESS_OPTIONS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: 'faellesvaskeri', label: 'Fællesvaskeri', icon: IcoWash },
  { key: 'loftrum', label: 'Loft-/kælderrum', icon: IcoBox },
  { key: 'garage', label: 'Garage', icon: IcoParking },
  { key: 'cykelparkering', label: 'Cykelparkering', icon: IcoBike },
  { key: 'gaardanlaeg', label: 'Fælles gårdanlæg', icon: IcoTree },
  { key: 'have', label: 'Have/terrasse', icon: IcoSun },
  { key: 'andet', label: 'Andet', icon: IcoPlus },
]

const MONTHS = [
  'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'December',
]

// ─── PrefilledNote ────────────────────────────────────────────────────────────
function PrefilledNote({ field, state }: { field: string; state: WizardState }) {
  if (!state.prefilled_fields.includes(field)) return null
  return (
    <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>
      ✓ Forududfyldt fra lejemål
    </div>
  )
}

// ─── FreqPill ─────────────────────────────────────────────────────────────────
function FreqPill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, padding: '9px 14px', borderRadius: 20, cursor: 'pointer', textAlign: 'center',
        border: `1px solid ${selected ? BLUE : '#d1d5db'}`,
        background: selected ? BLUE : 'white',
        fontSize: 13, fontWeight: 500, color: selected ? 'white' : '#374151',
        transition: 'all 0.12s',
      }}
    >
      {label}
    </div>
  )
}

// ─── FriLejeRadio ─────────────────────────────────────────────────────────────
function FriLejeRadio({ selected, onSelect, label }: {
  selected: boolean; onSelect: () => void; label: string
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 12px', borderRadius: 7, marginBottom: 6, cursor: 'pointer',
        border: `1px solid ${selected ? BLUE : '#d1d5db'}`,
        background: selected ? '#eef2ff' : 'white',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
        border: `2px solid ${selected ? BLUE : '#d1d5db'}`,
        background: selected ? BLUE : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
      </div>
      <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
        {label}
      </span>
    </div>
  )
}

// ─── AccessCheckbox ───────────────────────────────────────────────────────────
function AccessCheckbox({ icon, label, checked, onChange }: {
  icon: React.ReactNode; label: string; checked: boolean; onChange: () => void
}) {
  return (
    <div
      onClick={onChange}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 10px', borderRadius: 7, marginBottom: 5, cursor: 'pointer',
        border: `1px solid ${checked ? BLUE : '#e8eaed'}`,
        background: checked ? '#eef2ff' : 'white',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 7, flexShrink: 0,
        background: '#f7f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: checked ? BLUE : '#9ca3af',
      }}>
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: 13, color: '#374151', fontWeight: checked ? 500 : 400 }}>
        {label}
      </span>
      <div style={{
        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${checked ? BLUE : '#d1d5db'}`,
        background: checked ? BLUE : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
            <polyline points="2 6 5 9 10 3"/>
          </svg>
        )}
      </div>
    </div>
  )
}

// ─── SectionGenerelt ──────────────────────────────────────────────────────────
export function SectionGenerelt({ state, onChange }: { state: WizardState; onChange: (p: Partial<WizardState>) => void }) {
  const toggleAccess = (key: string) => {
    const turningOn = !state.access_rights.includes(key)
    const next = turningOn
      ? [...state.access_rights, key]
      : state.access_rights.filter(k => k !== key)
    // Seed one empty "Andet" row on enable; clear all rows on disable.
    if (key === 'andet') {
      onChange({
        access_rights: next,
        access_other: turningOn ? ((state.access_other ?? []).length ? state.access_other : ['']) : [],
      })
      return
    }
    onChange({ access_rights: next })
  }

  return (
    <div>
      {/* Lejemålstype */}
      <SectionBlock icon={IcoHouse} title="Lejemålstype" subtitle="Hvilken type lejemål udlejes der?">
        <FieldRow label="Hvilken boligtype udlejes der?" required>
          <RadioPillGrid
            options={BOLIGTYPE_OPTIONS}
            value={state.lease_display_type}
            onChange={v => onChange({ lease_display_type: v as WizardState['lease_display_type'] })}
          />
        </FieldRow>
        <Expandable open={state.lease_display_type === 'andet'}>
          <FieldRow label="Beskriv boligtypen" required>
            <input style={inputStyle} placeholder="F.eks. sommerhus, kolonihavehus…"
              value={state.lease_display_other} onChange={e => onChange({ lease_display_other: e.target.value })} />
          </FieldRow>
        </Expandable>
        <FieldRow label="Er lejeforholdet et fremleje?">
          <Toggle on={state.is_sublease} onToggle={() => onChange({ is_sublease: !state.is_sublease })} />
        </FieldRow>
      </SectionBlock>

      {/* Areal & værelser */}
      <SectionBlock icon={IcoArea} title="Areal & værelser" subtitle="Hvor stort er lejemålet?">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <FieldRow label="Areal" required>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} type="number" value={state.area_m2}
                onChange={e => onChange({ area_m2: e.target.value })} />
              <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>m²</span>
            </div>
            <PrefilledNote field="area_m2" state={state} />
          </FieldRow>
          <FieldRow label="Areal til erhverv">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} type="number" value={state.commercial_area_m2}
                onChange={e => onChange({ commercial_area_m2: e.target.value })} />
              <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>m²</span>
            </div>
          </FieldRow>
        </div>
        <FieldRow label="Antal værelser" required>
          <Stepper
            value={parseInt(state.room_count || '0', 10)}
            onChange={v => onChange({ room_count: String(v) })}
          />
          <PrefilledNote field="room_count" state={state} />
        </FieldRow>
      </SectionBlock>

      {/* Husleje */}
      <SectionBlock icon={IcoCash} title="Husleje" subtitle="Hvad er huslejen og hvordan foregår betaling?">
        <FieldRow label="Månedlig husleje (ekskl. forbrug)" required>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input style={{ ...inputStyle, flex: 1 }} type="number" value={state.monthly_rent_dkk}
              onChange={e => onChange({ monthly_rent_dkk: e.target.value })} />
            <span style={{ fontSize: 13, color: '#9ca3af' }}>kr.</span>
          </div>
        </FieldRow>
        <FieldRow label="Huslejen m.v. skal betales">
          <div style={{ display: 'flex', gap: 8 }}>
            <FreqPill
              label="Månedligt"
              selected={(state.payment_frequency ?? 'monthly') === 'monthly'}
              onClick={() => onChange({ payment_frequency: 'monthly' })}
            />
            <FreqPill
              label="Kvartalsvist"
              selected={(state.payment_frequency ?? 'monthly') === 'quarterly'}
              onClick={() => onChange({ payment_frequency: 'quarterly' })}
            />
          </div>
        </FieldRow>
        <FieldRow label={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Er lejeforholdet omfattet af reglerne om fri leje?
            <HelpTooltip text="Fri leje er undtaget lejelovens huslejekontrol, jf. § 54. Det gælder bl.a. for ejendomme taget i brug efter 31. december 1991. Forkert klassificering kan føre til tilbagebetaling af husleje." />
          </div>
        }>
          <Toggle testId="fri-leje-toggle" on={state.free_rent} onToggle={() => onChange({ free_rent: !state.free_rent })} />
        </FieldRow>
        <Expandable open={state.free_rent}>
          <p style={{ fontSize: 12, color: '#92400e', marginBottom: 10, lineHeight: 1.5 }}>
            Vælg begrundelsen for fri leje, jf. lejelovens § 54. Mindst ét grundlag skal være opfyldt.
          </p>
          {FRI_LEJE_OPTIONS.map(opt => {
            const predefined = ['post1991', 'erhverv1991', 'tagetage']
            const isCustom = opt.value === 'custom'
            const selected = isCustom
              ? !predefined.includes(state.free_rent_reason) && state.free_rent_reason !== ''
              : state.free_rent_reason === opt.value
            return (
              <FriLejeRadio
                key={opt.value}
                label={opt.label}
                selected={selected}
                onSelect={() => onChange({ free_rent_reason: isCustom ? 'custom' : opt.value })}
              />
            )
          })}
          {!['', 'post1991', 'erhverv1991', 'tagetage'].includes(state.free_rent_reason) && (
            <div style={{ marginTop: 4 }}>
              <input style={inputStyle} placeholder="Beskriv begrundelse for fri leje…"
                value={state.free_rent_reason === 'custom' ? '' : state.free_rent_reason}
                onChange={e => onChange({ free_rent_reason: e.target.value || 'custom' })} />
            </div>
          )}
        </Expandable>
        <FieldRow label={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Skal der ske årlig lejeregulering efter nettoprisindekset?
            <HelpTooltip text="Nettoprisindeksregulering giver udlejer ret til at forhøje huslejen årligt i takt med Danmarks Statistiks nettoprisindeks. Reguleringen kan tidligst ske 1 år efter lejeaftalens begyndelse, jf. lejelovens § 53." />
          </div>
        }>
          <Toggle on={state.nettoprisindeks} onToggle={() => {
            const turning_on = !state.nettoprisindeks
            const next_jan1 = (() => {
              const now = new Date()
              const year = now.getMonth() >= 0 ? now.getFullYear() + 1 : now.getFullYear()
              return `${year}-01-01`
            })()
            onChange({
              nettoprisindeks: turning_on,
              ...(!state.regulation_first_date && turning_on ? { regulation_first_date: next_jan1 } : {}),
            })
          }} />
        </FieldRow>
        <Expandable open={state.nettoprisindeks}>
          {/* Sentence: "d. [day] [month] reguleres den gældende leje med stigningen i nettoprisindekset fra [month] måned..." */}
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, marginBottom: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
            <span>d.</span>
            <select style={{ ...inputStyle, width: 60, padding: '4px 6px' }} value={state.regulation_day}
              onChange={e => onChange({ regulation_day: e.target.value })}>
              {Array.from({ length: 28 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>{i + 1}.</option>
              ))}
            </select>
            <select style={{ ...inputStyle, width: 110, padding: '4px 6px' }} value={state.regulation_month}
              onChange={e => onChange({ regulation_month: e.target.value })}>
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={String(i + 1)}>{m}</option>
              ))}
            </select>
            <span>reguleres den gældende leje med stigningen i nettoprisindekset fra</span>
            <select style={{ ...inputStyle, width: 120, padding: '4px 6px' }} value={state.regulation_index_from}
              onChange={e => onChange({ regulation_index_from: e.target.value })}>
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={String(i + 1)}>{m}</option>
              ))}
            </select>
            <span>måned året før til</span>
            <select style={{ ...inputStyle, width: 120, padding: '4px 6px' }} value={state.regulation_index_to}
              onChange={e => onChange({ regulation_index_to: e.target.value })}>
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={String(i + 1)}>{m}</option>
              ))}
            </select>
            <span>måned forud for reguleringstidspunktet.</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <FieldRow label="Lejen reguleres første gang den">
              <input style={inputStyle} type="date" value={state.regulation_first_date}
                onChange={e => onChange({ regulation_first_date: e.target.value })} />
            </FieldRow>
            <FieldRow label="Maksimum % pr. år">
              <input style={inputStyle} type="number" placeholder="f.eks. 4" min="0" max="100" step="0.1"
                value={state.regulation_max_pct}
                onChange={e => onChange({ regulation_max_pct: e.target.value })} />
            </FieldRow>
          </div>
          <div style={{
            background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 7,
            padding: '10px 12px', fontSize: 12, color: '#4338ca', lineHeight: 1.55,
          }}>
            Nettoprisindeksregulering giver udlejer ret til at forhøje huslejen årligt i takt med Danmarks Statistiks nettoprisindeks. Reguleringen kan tidligst ske 1 år efter lejeaftalens begyndelse, jf. lejelovens § 53.
          </div>
        </Expandable>
      </SectionBlock>

      {/* Indflytning */}
      <SectionBlock icon={IcoCalendar} title="Indflytning" subtitle="Hvornår overtages lejemålet?">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FieldRow label="Indflytningsdato" required>
            <input style={inputStyle} type="date" value={state.start_date}
              onChange={e => onChange({ start_date: e.target.value })} />
          </FieldRow>
        </div>
        <FieldRow label="Tidsbegrænset lejeforhold">
          <Toggle on={state.is_fixed_term} onToggle={() => onChange({ is_fixed_term: !state.is_fixed_term })} />
        </FieldRow>
        <Expandable open={state.is_fixed_term}>
          <FieldRow label="Slutdato" required>
            <input style={inputStyle} type="date" value={state.end_date}
              onChange={e => onChange({ end_date: e.target.value })} />
          </FieldRow>
        </Expandable>
      </SectionBlock>

      {/* Adgang & brugsret */}
      <SectionBlock icon={IcoKey} title="Adgang & brugsret" subtitle="Hvad har lejeren adgang og brugsret til?">
        <p style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
          Lejeren har adgang og brugsret til følgende:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          {ACCESS_OPTIONS.map(opt => (
            <div key={opt.key}>
              <AccessCheckbox
                icon={opt.icon}
                label={opt.label}
                checked={state.access_rights.includes(opt.key)}
                onChange={() => toggleAccess(opt.key)}
              />
              {(opt.key === 'loftrum' || opt.key === 'garage') && state.access_rights.includes(opt.key) && (
                <div style={{ marginTop: -4, marginBottom: 6, paddingLeft: 48 }}>
                  <input
                    style={{ ...inputStyle, width: 160, fontSize: 12 }}
                    placeholder="Nr. (f.eks. 12)"
                    value={(state.access_numbers ?? {})[opt.key] ?? ''}
                    onChange={e => onChange({ access_numbers: { ...(state.access_numbers ?? {}), [opt.key]: e.target.value } })}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <Expandable open={state.access_rights.includes('andet')}>
          <FieldRow label="Beskriv adgang">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(state.access_other ?? []).map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={row}
                    onChange={e => {
                      const next = [...(state.access_other ?? [])]
                      next[i] = e.target.value
                      onChange({ access_other: next })
                    }}
                    placeholder="F.eks. depotrum nr. 4…" />
                  <button
                    type="button"
                    onClick={() => onChange({ access_other: (state.access_other ?? []).filter((_, j) => j !== i) })}
                    style={{ width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#9ca3af', flexShrink: 0 }}>
                    ×
                  </button>
                </div>
              ))}
              {(state.access_other ?? []).length < 3 && (
                <button
                  type="button"
                  onClick={() => onChange({ access_other: [...(state.access_other ?? []), ''] })}
                  style={{ fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0, alignSelf: 'flex-start' }}>
                  + Tilføj
                </button>
              )}
            </div>
          </FieldRow>
        </Expandable>
      </SectionBlock>
    </div>
  )
}
